"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useAxios from "src/hooks/useAxios";
import { Button } from "src/v1/ui/button-two/button";
import { Input } from "src/v1/ui/input/input";
import { Label } from "src/v1/ui/label/label";
import { RadioGroup, RadioGroupItem } from "src/v1/ui/radio/radio-group";
import { ScrollArea } from "src/v1/ui/scroll-area/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/v1/ui/select/select";
import { useLogging } from "../hooks/useLogging";

// Extend Window interface for karrotPixel
declare global {
  interface Window {
    karrotPixel?: {
      track: (event: string) => void;
    };
  }
}

export interface FormData {
  금액: number;
  추정요건사실: string;
  집행권원종류: string[];
  채권자이름: string;
  preBondId: string;
  방문채널?: string;
}

export default function LegalConsultationPage() {
  const { axiosGet } = useAxios();
  const [formData, setFormData] = useState<FormData>({
    금액: 0,
    추정요건사실: "",
    집행권원종류: [],
    채권자이름: "",
    preBondId: "",
    방문채널: "",
  });
  const navigate = useNavigate();
  const { logEvent, getPreviousPath } = useLogging();

  // Check for preBondId in URL search parameters
  useEffect(() => {
    const createNewPreBondId = async () => {
      try {
        const res = await axiosGet("/apiOpen/b2c/v3/preBondId/new");
        if (res.code < 0) {
          toast.error(res.msg);
          return;
        }
        setFormData((prev) => ({ ...prev, preBondId: res.extra }));
      } catch (error) {
        console.error(error);
      }
    };
    const urlParams = new URLSearchParams(window.location.search);
    const preBondId = urlParams.get("preBondId");
    if (preBondId) {
      logEvent({
        name: "Page",
        value: "webform",
        type: "Page",
        from: "kakaoChat",
      });
      setFormData((prev) => ({ ...prev, preBondId }));
    } else {
      createNewPreBondId();
      logEvent({
        name: "Page",
        value: "webform",
        type: "Page",
        from: getPreviousPath(),
      });
    }
  }, []);

  // Danggeun Market tracking script
  useEffect(() => {
    if (window.karrotPixel && typeof window.karrotPixel.track === "function") {
      window.karrotPixel.track("ViewContent");
    }
  }, []);
  // Loading animation states

  useEffect(() => {
    localStorage.setItem("formData", JSON.stringify(formData));
  }, [formData]);

  const updateFormData = (field: keyof FormData, value: string | string[]) => {
    if (field === "금액") {
      setFormData((prev) => ({ ...prev, [field]: Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const isFormValid = () => {
    return (
      // formData.채권자이름.trim() !== "" &&
      formData.금액 > 0 &&
      formData.추정요건사실 &&
      formData.집행권원종류.length > 0 &&
      formData.방문채널
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 break-keep">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-10">
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <h1 className="text-4xl font-bold text-gray-900">
                소송지원금 진단을 위한 정보를 입력해주세요
              </h1>
            </div>
          </div>

          <div className="space-y-12">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">기본 정보</h2>
              <div className="space-y-4">
                {/*<div className="space-y-4">*/}
                {/*  <Label*/}
                {/*    htmlFor="채권자이름"*/}
                {/*    className="text-xl font-bold text-gray-700 block text-left"*/}
                {/*  >*/}
                {/*    👤 채권자명*/}
                {/*  </Label>*/}
                {/*  <Input*/}
                {/*    id="채권자이름"*/}
                {/*    type="text"*/}
                {/*    placeholder="예: 홍길동"*/}
                {/*    value={formData.채권자이름}*/}
                {/*    onChange={(e) =>*/}
                {/*      updateFormData("채권자이름", e.target.value)*/}
                {/*    }*/}
                {/*    className="h-16 text-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-left"*/}
                {/*  />*/}
                {/*</div>*/}

                <div className="space-y-4">
                  <Label
                    htmlFor="amount"
                    className="text-xl font-bold text-gray-700 block text-left"
                  >
                    💰 받아야 할 금액
                  </Label>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="예: 5,000,000"
                    value={formData.금액 === 0 ? "" : formData.금액}
                    onChange={(e) => updateFormData("금액", e.target.value)}
                    className="h-16 text-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-left"
                  />
                  {formData.금액 > 0 && (
                    <div className="text-left">
                      <span className="text-2xl font-bold text-blue-600">
                        {formData.금액.toLocaleString()}원
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Reason Section */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">
                어떤 이유로 이 돈을 받아야 하나요?
              </h2>
              <div className="space-y-4">
                <Select
                  value={formData.추정요건사실}
                  onValueChange={(value) =>
                    updateFormData("추정요건사실", value)
                  }
                >
                  <SelectTrigger className="h-16 text-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 w-full text-left bg-white hover:bg-gray-50 transition-all duration-200 rounded-xl shadow-sm hover:shadow-md">
                    <SelectValue
                      placeholder="선택해주세요"
                      className="truncate text-gray-600"
                    >
                      {formData.추정요건사실}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white min-w-[300px] max-w-[90vw] md:max-w-[400px] max-h-[300px] border-2 border-gray-200 rounded-xl shadow-lg">
                    <ScrollArea className="h-[280px] max-h-[280px]">
                      {[
                        {
                          value: "대여금",
                          label: "대여금",
                          desc: "빌려줬는데 안 갚아요. (지인, 친구, 가족 포함)",
                        },
                        {
                          value: "매매대금",
                          label: "매매대금",
                          desc: "물건·서비스 주고, 돈 못 받았어요. (사업 거래 포함)",
                        },
                        {
                          value: "투자금(개인)",
                          label: "투자금",
                          desc: "투자했다가 돈을 못 돌려받고 있어요.",
                        },
                        {
                          value: "임대자 보증금",
                          label: "임대차보증금",
                          desc: "전세금, 월세 보증금 아직 안 돌려받음",
                        },
                        {
                          value: "임금",
                          label: "임금",
                          desc: "일은 했는데 돈을 못 받았어요.",
                        },
                        {
                          value: "계약금",
                          label: "계약금",
                          desc: "계약은 했는데 약속한 돈을 안 줘요.",
                        },
                        {
                          value: "손해배상",
                          label: "손해배상",
                          desc: "피해에 대한 보상으로 받아야 해요.",
                        },
                        {
                          value: "공사대금",
                          label: "공사대금",
                          desc: "공사 대금이 아직 안 돌려받았어요.",
                        },
                        {
                          value: "착오송금",
                          label: "착오송금",
                          desc: "착오로 주신 돈이 아직 안 돌려받았어요.",
                        },
                        {
                          value: "보이스피싱",
                          label: "보이스피싱",
                          desc: "보이스피싱으로 사기당했어요.",
                        },
                        {
                          value: "물품대금",
                          label: "물품대금",
                          desc: "물건을 팔고 돈을 못 받았어요.",
                        },
                        {
                          value: "약정금",
                          label: "약정금",
                          desc: "약정금한 돈을 아직 안 돌려받았어요.",
                        },
                        {
                          value: "계금",
                          label: "계금(곗돈)",
                          desc: "계금을 받아야 해요.",
                        },
                        {
                          value: "소송비용",
                          label: "소송비용",
                          desc: "소송비용을 받아야 해요.",
                        },
                        {
                          value: "구상금",
                          label: "구상금",
                          desc: "돈을 대신 갚아줘서 받아야 함.",
                        },
                        {
                          value: "횡령",
                          label: "횡령",
                          desc: "상대가 보관하던 내 돈을 돌려받아야 함.",
                        },
                        {
                          value: "other",
                          label: "기타",
                          desc: "추후 직접 입력해주시면 돼요",
                        },
                      ].map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="hover:bg-blue-50 focus:bg-blue-50 rounded-lg mx-2 my-1 transition-colors duration-150"
                        >
                          <div className="flex flex-col items-start gap-1">
                            <span className="font-semibold text-base">
                              {option.label}
                            </span>
                            <span className="text-sm text-gray-600 leading-relaxed">
                              {option.desc}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Legal Documents Section */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">
                해당되는 문서가 있으신가요?
              </h2>
              <div className="space-y-4">
                <Select
                  onValueChange={(value) => {
                    const currentArray = formData.집행권원종류;
                    const isSelected = currentArray.includes(value);

                    if (isSelected) {
                      // Remove if already selected
                      updateFormData(
                        "집행권원종류",
                        currentArray.filter((item) => item !== value)
                      );
                    } else {
                      // Add if not selected
                      updateFormData("집행권원종류", [...currentArray, value]);
                    }
                  }}
                >
                  <SelectTrigger className="h-16 text-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 w-full text-left bg-white hover:bg-gray-50 transition-all duration-200 rounded-xl shadow-sm hover:shadow-md">
                    <SelectValue
                      placeholder="문서를 선택해주세요 (복수 선택 가능)"
                      className="truncate text-gray-600"
                    >
                      {formData.집행권원종류.length > 0 ? (
                        <span className="text-blue-600 font-medium">
                          {formData.집행권원종류.length}개 선택됨
                        </span>
                      ) : (
                        "문서를 선택해주세요"
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white min-w-[300px] max-w-[90vw] md:max-w-[400px] max-h-[300px] border-2 border-gray-200 rounded-xl shadow-lg">
                    <ScrollArea className="h-[280px] max-h-[280px]">
                      {[
                        {
                          value: "없음",
                          label: "🤷 문서가 없어요",
                          desc: "소송은 아직 안했어요, 이런 건 받은 기억이 없어요",
                        },
                        {
                          value: "민사소송진행중",
                          label: "🤔 민사소송 완료단계",
                          desc: "민사소송이 거의 종결된 상태에요",
                        },
                        {
                          value: "판결문",
                          label: "🧾 판결문",
                          desc: "법원에서 상대방에게 '돈 갚으라'는 판결을 받은 문서",
                        },
                        {
                          value: "지급명령서",
                          label: "📝 지급명령서",
                          desc: "법원에서 상대방에게 지급하라고 명령한 간단한 판결서",
                        },
                        {
                          value: "공정증서",
                          label: "📑 공정증서",
                          desc: "공증사무소에서 만든, 강제집행이 가능한 계약서",
                        },
                        {
                          value: "형사배상명령",
                          label: "⚖️ 형사배상명령",
                          desc: "형사재판 결과로 돈 물어내라는 결정",
                        },
                      ].map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="hover:bg-blue-50 focus:bg-blue-50 rounded-lg mx-2 my-1 transition-colors duration-150"
                        >
                          <div className="flex flex-col items-start gap-1">
                            <span className="font-semibold text-base">
                              {option.label}
                            </span>
                            <span className="text-sm text-gray-600 leading-relaxed">
                              {option.desc}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>

                {/* Selected Documents Display */}
                {formData.집행권원종류.length > 0 && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-3">
                      선택된 문서:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {formData.집행권원종류.map((document) => {
                        const documentOption = [
                          { value: "없음", label: "🤷 문서가 없어요" },
                          {
                            value: "민사소송진행중",
                            label: "🤔 민사소송 완료단계",
                          },
                          { value: "판결문", label: "🧾 판결문" },
                          { value: "지급명령서", label: "📝 지급명령서" },
                          { value: "공정증서", label: "📑 공정증서" },
                          { value: "형사배상명령", label: "⚖️ 형사배상명령" },
                        ].find((option) => option.value === document);

                        return (
                          <span
                            key={document}
                            className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors duration-150"
                          >
                            {documentOption?.label || document}
                            <button
                              type="button"
                              onClick={() => {
                                updateFormData(
                                  "집행권원종류",
                                  formData.집행권원종류.filter(
                                    (item) => item !== document
                                  )
                                );
                              }}
                              className="ml-2 text-blue-600 hover:text-blue-800 font-bold"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Visit Channel Section */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">
                어떤 채널을 통해 방문하셨나요?
              </h2>
              <div className="space-y-4">
                <RadioGroup
                  value={formData.방문채널}
                  onValueChange={(value: string) =>
                    updateFormData("방문채널", value)
                  }
                  className="grid grid-cols-1 tablet:grid-cols-2 gap-4"
                >
                  {[
                    {
                      value: "네이버 검색",
                      label: "🔍 네이버에서 특정 키워드 검색 후 결과 클릭",
                    },
                    {
                      value: "네이버 배너",
                      label:
                        "📢 (검색하지 않았으나) 네이버에서 광고가 떠서 클릭",
                    },
                    {
                      value: "카카오 배너",
                      label: "💬 카카오톡에서 광고가 떠서 클릭",
                    },
                    {
                      value: "구글 검색",
                      label: "🔍 구글에서 특정 키워드 검색 후 결과 클릭",
                    },
                    {
                      value: "구글 배너",
                      label:
                        "📢 (검색하지 않았으나) 구글, 유튜브 등에서 광고가 떠서 클릭",
                    },
                    {
                      value: "블로그",
                      label:
                        "📝 채권도시 블로그를 방문하였다가 웹사이트로 연결되는 링크 클릭",
                    },
                    {
                      value: "카페/커뮤니티",
                      label: "👥 카페, 커뮤니티 등에서 알게 되어 웹사이트 접속",
                    },
                  ].map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 cursor-pointer"
                    >
                      <RadioGroupItem
                        value={option.value}
                        id={option.value}
                        className="text-blue-600"
                      />
                      <label
                        htmlFor={option.value}
                        className="text-lg font-medium text-gray-900 cursor-pointer flex-1"
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <Button
              onClick={() => navigate("/analyze")}
              disabled={!isFormValid()}
              className="w-full h-20 bg-blue-600 hover:bg-blue-700 text-2xl font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
              size="lg"
              id="calculation-predata"
            >
              🔍 지원금 계산 결과
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
